import * as React from "react";
import { Box, IconButton, useTheme, useMediaQuery } from "@mui/material";
import { SvgIcon } from "@mui/material";

import { useFeatureFlag } from "../../hooks/useFeatureFlag.tsx";
import VoiceRecorder from "./VoiceRecorder.tsx";
import StopIcon from "@mui/icons-material/Stop";

// Custom thin arrow component
const ThinArrowUp = (props: any) => (
  <SvgIcon {...props}>
    <path
      d="M12 20V5 M6 11l6-6 6 6"
      fill="none"
      stroke="currentColor"
      strokeWidth="1"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </SvgIcon>
);

type Props = {
  value: string;
  onChange: (v: string) => void;
  onSubmit: (textToSubmit?: string) => void;
  onStop: () => void;
  placeholder: string;
  disabled?: boolean;
  isCircleView?: boolean;
};

export function SearchBarCompact({
  value,
  onChange,
  onSubmit,
  onStop,
  placeholder,
  disabled,
  isCircleView = false,
}: Props) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isVoiceAssistantEnabled = useFeatureFlag("voice_assistant");

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      onSubmit();
    }
  };

  // sizes to match the mock
  const pillHeight = 36; // total height of the bar
  const iconSize = 28; // colored circle diameter
  const ring = 4; // white ring thickness incl. spacing

  return (
    <Box
      sx={{
        position: "relative",
        width: isCircleView
          ? { xs: "98%", sm: "95%", md: "92%", lg: "85%" }
          : "100%",
        height: { xs: pillHeight - 10, sm: pillHeight }, // Use fixed height for consistency
        borderRadius: pillHeight / 2,
        bgcolor: "common.white",
        display: "flex",
        alignItems: "center",
        // Ensure proper box sizing
        boxSizing: "border-box",
        // small left padding; right space reserved by pr on InputBase
        pl: { xs: "16px", sm: "20px" }, // Slightly less padding on mobile
      }}
    >
      <Box
        component="input"
        disabled={disabled}
        value={value}
        placeholder={placeholder}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          onChange(e.target.value)
        }
        onKeyDown={handleKeyDown}
        sx={{
          width: `calc(100% - ${iconSize + ring * 2 + 8}px)`, // leave room for the button
          height: { xs: `${pillHeight - 4 - 10}px`, sm: `${pillHeight - 4}px` },
          border: "none",
          outline: "none",
          background: "transparent",
          fontSize: { xs: 14, sm: 14 },
          fontFamily: "inherit",
          color: "inherit",
          lineHeight: 1.4, // More natural line height for better cursor alignment
          padding: "2px 0", // Small vertical padding for better text baseline
          margin: 0,
          boxSizing: "border-box",
          // Ensure proper text baseline alignment
          verticalAlign: "middle",
          // Placeholder styles
          "&::placeholder": {
            color: "rgba(105,105,105,0.4)",
            opacity: 1,
            fontSize: { xs: 14, sm: 14 },
            lineHeight: 1.4, // Match input line height
          },
          // Webkit placeholder
          "&::-webkit-input-placeholder": {
            color: "rgba(105,105,105,0.4)",
            opacity: 1,
            WebkitTextFillColor: "rgba(105,105,105,0.4)",
            lineHeight: 1.4,
          },
          // Firefox placeholder
          "&::-moz-placeholder": {
            color: "rgba(105,105,105,0.4)",
            opacity: 1,
            lineHeight: 1.4,
          },
          // IE/Edge placeholder
          "&:-ms-input-placeholder": {
            color: "rgba(105,105,105,0.4)",
            opacity: 1,
            lineHeight: 1.4,
          },
          // Focus styles
          "&:focus": {
            outline: "none",
          },
          // Remove default input styling
          WebkitAppearance: "none",
          appearance: "none",
          resize: "none",
        }}
        aria-label="Search"
        data-testid="search-input"
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck={false}
      />

      {/* Render Voice Recorder icon only when user logged-in and feature is enabled */}
      {!isCircleView && isVoiceAssistantEnabled && (
        <VoiceRecorder isMobile={isMobile} disabled={disabled} />
      )}

      {/* End button without white ring */}
      <IconButton
        aria-label={disabled ? "Stop" : "Send"}
        onClick={disabled ? () => onStop() : () => onSubmit()}
        tabIndex={-1}
        sx={{
          position: "absolute",
          right: 4,
          top: "50%",
          transform: "translateY(-50%)",
          m: 0,
          p: 0,
          width: { xs: iconSize - 10, sm: iconSize },
          height: { xs: iconSize - 10, sm: iconSize },
          borderRadius: "50%",
          bgcolor: theme.palette.primary.main,
          color: "white",
          "&:hover": { bgcolor: theme.palette.primary.main },
        }}
      >
        {disabled ? (
          <StopIcon fontSize={isMobile ? "small" : "medium"} />
        ) : (
          <ThinArrowUp
            fontSize={isMobile ? "small" : "medium"}
            sx={{ color: "white" }}
          />
        )}
      </IconButton>
    </Box>
  );
}
